import { useEffect, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi, getSelectedClientId, setSelectedClientId, setFallbackClientId,
  subscribeSelectedClientId, type Client,
} from '../api';

/** Bez výslovné volby padne admin na tuhle organizaci. */
const DEFAULT_CLIENT_NAME = 'hvozdici';

function pickDefault(clients: Client[]): Client {
  return (
    clients.find((c) => c.name.toLowerCase().includes(DEFAULT_CLIENT_NAME)) ?? clients[0]
  );
}

/**
 * Rozsah dat celé aplikace. Backend filtruje úplně všechno podle client_id, takže
 * je to jediné místo, kde se rozhoduje, čí data se zobrazují.
 *
 * Dva režimy podle API klíče:
 *  - admin  — /admin/clients projde, organizaci volí uživatel; výběr se drží
 *             v localStorage a musí být vždycky nějaký, jinak by middleware nastavil
 *             clientId = null a data by se nefiltrovala vůbec.
 *  - klient — /admin/clients vrátí chybu; organizace je dána klíčem a hlavička
 *             X-Client-Id se dopočítá z epochy kvůli routám /pairing/*.
 */
export function useClientScope() {
  const qc = useQueryClient();

  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: adminApi.getClients,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const isAdmin = clients.isSuccess && (clients.data?.length ?? 0) > 0;
  const selected = useSyncExternalStore(subscribeSelectedClientId, getSelectedClientId);

  // Admin bez volby: dosaď výchozí organizaci a načti data znovu, ať se nezobrazí
  // směs všech tenantů.
  useEffect(() => {
    if (!isAdmin || !clients.data?.length) return;

    const known = clients.data.some((c) => String(c.id) === selected);
    if (selected && known) return;

    setSelectedClientId(String(pickDefault(clients.data).id));
    qc.clear();
  }, [isAdmin, clients.data, selected, qc]);

  // Fallback z epochy dává smysl jen u klientského klíče. U admina by bez volby
  // poslal náhodného tenanta, tak ho drž vypnutý.
  useEffect(() => {
    if (isAdmin) setFallbackClientId(null);
  }, [isAdmin]);

  const switchTo = (id: string) => {
    if (id === getSelectedClientId()) return;
    setSelectedClientId(id);
    // Data starého tenanta nesmí zůstat v cache ani na okamžik.
    qc.clear();
  };

  return {
    isAdmin,
    clients: clients.data ?? [],
    selectedClientId: selected,
    switchTo,
  };
}
